import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api.js';
import { fmtDate, relTime } from '../lib/format.js';
import { IconActivity, IconCheck, IconPlus, IconUsers } from '../components/Icons.js';
import { getCurrentState, pushSupported, subscribe, unsubscribe, type PushState } from '../lib/web-push.js';
import { DEFAULT_ACCENT, previewAccentColor, setBranding, useBranding } from '../lib/branding.js';

interface User {
  discord_id: string;
  discord_username: string;
  role: 'admin' | 'operator' | 'viewer';
  added_at: string;
  last_login: string | null;
  avatar_url?: string;
}

interface Machine {
  machine_name: string;
  machine_label?: string;
  online: boolean;
  last_seen?: string | null;
  agent_version?: string;
  server_count: number;
}

type Tab = 'machines' | 'users' | 'branding' | 'discord' | 'security' | 'system';

const TABS: Array<{ key: Tab; label: string; icon: React.ReactNode; adminOnly?: boolean }> = [
  { key: 'machines', label: 'Machines', icon: <IconActivity className="w-3.5 h-3.5" /> },
  { key: 'users',    label: 'Users',    icon: <IconUsers className="w-3.5 h-3.5" />, adminOnly: true },
  { key: 'branding', label: 'Branding', icon: <PaintIcon />, adminOnly: true },
  { key: 'discord',  label: 'Discord',  icon: <DiscordTabIcon />, adminOnly: true },
  { key: 'security', label: 'Security', icon: <ShieldIcon /> },
  { key: 'system',   label: 'System',   icon: <span className="font-mono text-[11px]">{'{}'}</span> },
];

export default function Settings({ me }: { me: { discord_id: string; role: string } }) {
  const [tab, setTab] = useState<Tab>('machines');
  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
      </header>

      <div className="tab-bar w-full lg:max-w-3xl flex-wrap">
        {TABS.filter((t) => !t.adminOnly || me.role === 'admin').map((t) => {
          const isActive = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`tab-item ${isActive ? 'tab-item-active' : ''}`}
              aria-pressed={isActive}
            >
              <span className={isActive ? 'text-accent' : 'text-fg-dim'}>{t.icon}</span>
              <span>{t.label}</span>
            </button>
          );
        })}
      </div>

      {tab === 'machines' && <MachinesTab isAdmin={me.role === 'admin'} />}
      {tab === 'users'    && me.role === 'admin' && <UsersTab me={me} />}
      {tab === 'branding' && me.role === 'admin' && <BrandingTab />}
      {tab === 'discord'  && me.role === 'admin' && <DiscordTab />}
      {tab === 'security' && <SecurityTab />}
      {tab === 'system'   && <SystemTab />}
    </div>
  );
}

/** Small <img> with a stateful fallback that *resets* when the URL changes.
 *  Important here because hash-versioned upload URLs change on every save and
 *  we don't want a previous failure to keep the new image hidden. The `key`
 *  prop forces React to fully remount the img on URL change so no inline
 *  state from a prior render lingers. */
function LogoPreview({ url }: { url: string }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [url]);
  if (!url || failed) {
    return <span className="w-7 h-7 rounded-md bg-bg-card border border-bg-border shrink-0" aria-hidden />;
  }
  return (
    <img
      key={url}
      src={url}
      alt=""
      className="w-7 h-7 rounded-md object-cover bg-bg-card border border-bg-border shrink-0"
      onError={() => setFailed(true)}
    />
  );
}

function PaintIcon() {
  return (
    <svg
      width={14} height={14} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round"
      aria-hidden className="shrink-0"
    >
      <path d="M19 11h2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-9h2" />
      <rect x="3" y="3" width="18" height="8" rx="1" />
      <path d="M12 11v11" />
    </svg>
  );
}

function DiscordTabIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="currentColor" aria-hidden className="shrink-0">
      <path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.331c-1.182 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
    </svg>
  );
}

const ACCENT_PRESETS = [
  { label: 'Emerald (default)', hex: DEFAULT_ACCENT },
  { label: 'Mint',              hex: '#5eead4' },
  { label: 'Cyan',              hex: '#22d3ee' },
  { label: 'Sky',               hex: '#60a5fa' },
  { label: 'Indigo',            hex: '#818cf8' },
  { label: 'Violet',            hex: '#a78bfa' },
  { label: 'Pink',              hex: '#f472b6' },
  { label: 'Rose',              hex: '#fb7185' },
  { label: 'Amber',             hex: '#fbbf24' },
  { label: 'Lime',              hex: '#a3e635' },
];

function BrandingTab() {
  const branding = useBranding();
  const [name, setName] = useState(branding.name);
  const [tagline, setTagline] = useState(branding.tagline);
  const [logoUrl, setLogoUrl] = useState(branding.logo_url);
  const [accent, setAccent] = useState(branding.accent_color || DEFAULT_ACCENT);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setName(branding.name);
    setTagline(branding.tagline);
    setLogoUrl(branding.logo_url);
    setAccent(branding.accent_color || DEFAULT_ACCENT);
  }, [branding.name, branding.tagline, branding.logo_url, branding.accent_color]);

  // Apply the picked color live as the user clicks swatches. The cleanup
  // is split into a separate effect that runs only on unmount, otherwise
  // each swatch click would briefly flash the saved color before the new
  // selection paints.
  useEffect(() => {
    previewAccentColor(accent);
  }, [accent]);
  const savedAccentRef = useRef(branding.accent_color);
  useEffect(() => { savedAccentRef.current = branding.accent_color; }, [branding.accent_color]);
  useEffect(() => {
    return () => previewAccentColor(savedAccentRef.current || DEFAULT_ACCENT);
  }, []);

  const savedAccent = branding.accent_color || DEFAULT_ACCENT;
  const dirty =
    name !== branding.name ||
    tagline !== branding.tagline ||
    logoUrl !== branding.logo_url ||
    accent.toLowerCase() !== savedAccent.toLowerCase();
  // Recognize both the current `/branding/<filename>` form and the legacy
  // `/api/panel/logo[...]` form so the chip and disabled URL field still
  // make sense for older saved configs.
  const isUpload = logoUrl.startsWith('/branding/') || logoUrl.startsWith('/api/panel/logo');

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      // Save '' for the default emerald so we don't accumulate a redundant
      // override when the user resets.
      const accentToSave = accent.toLowerCase() === DEFAULT_ACCENT ? '' : accent.toLowerCase();
      const res = await api.patch<{ name: string; tagline: string; logo_url: string; accent_color?: string }>(
        '/api/panel/config',
        { name, tagline, logo_url: logoUrl, accent_color: accentToSave },
      );
      // If the server doesn't echo accent_color back (e.g. older hub that
      // hasn't been redeployed with the new schema), fall back to what we
      // sent so the UI doesn't snap back to the default. Saving will then
      // start working as expected once the server is updated.
      const accentEcho = typeof res.accent_color === 'string' ? res.accent_color : accentToSave;
      setBranding({
        name: res.name,
        tagline: res.tagline,
        logo_url: res.logo_url,
        accent_color: accentEcho,
      });
      setSavedAt(Date.now());
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const upload = async (file: File) => {
    setError(null);
    if (file.size === 0) { setError('File is empty'); return; }
    if (file.size > 2 * 1024 * 1024) { setError('File is larger than 2 MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file, file.name);
      const res = await fetch('/api/panel/logo', { method: 'POST', body: fd, credentials: 'include' });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `upload failed (${res.status})`);
      }
      const data = await res.json() as { logo_url: string; name: string; tagline: string; accent_color?: string };
      setLogoUrl(data.logo_url);
      setBranding({
        name: data.name,
        tagline: data.tagline,
        logo_url: data.logo_url,
        accent_color: data.accent_color ?? '',
      });
      setSavedAt(Date.now());
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setUploading(false);
    }
  };

  const removeUpload = async () => {
    setError(null);
    setBusy(true);
    try {
      const data = await api.delete<{ logo_url: string; name: string; tagline: string; accent_color?: string }>('/api/panel/logo');
      setLogoUrl(data.logo_url ?? '');
      setBranding({
        name: data.name,
        tagline: data.tagline,
        logo_url: data.logo_url ?? '',
        accent_color: data.accent_color ?? '',
      });
      setSavedAt(Date.now());
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="space-y-4 max-w-2xl">
      <div className="card p-5 space-y-4">
        <header>
          <h3 className="font-semibold text-fg">Branding</h3>
          <p className="text-sm text-fg-muted mt-1">
            Override the panel name and logo shown in the sidebar, login screen, and browser tab.
          </p>
        </header>

        <div>
          <label className="label">Panel name</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Clean Panel" maxLength={64} />
        </div>
        <div>
          <label className="label">Tagline</label>
          <input className="input" value={tagline} onChange={(e) => setTagline(e.target.value)} placeholder="Rust server control" maxLength={128} />
        </div>

        <div className="space-y-2">
          <label className="label">Logo</label>

          <div
            onDragOver={(e) => { e.preventDefault(); if (!uploading) setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              if (uploading) return;
              const f = e.dataTransfer.files[0];
              if (f) upload(f);
            }}
            onClick={() => { if (!uploading) fileInputRef.current?.click(); }}
            className={`rounded-lg border border-dashed p-4 text-center transition-colors ${
              uploading
                ? 'border-bg-border bg-bg-raised/30 cursor-wait pointer-events-none opacity-80'
                : dragOver
                  ? 'border-accent bg-accent/5 cursor-pointer'
                  : 'border-bg-border hover:border-bg-ring hover:bg-bg-hover/30 cursor-pointer'
            }`}
            role="button"
            tabIndex={0}
            aria-busy={uploading}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) upload(f);
                e.target.value = '';
              }}
            />
            <div className="text-sm text-fg-muted flex items-center justify-center gap-2">
              {uploading && <span className="inline-block w-3.5 h-3.5 rounded-full border-2 border-current border-t-transparent animate-spin" />}
              {uploading ? 'Uploading…' : 'Drop an image here or click to browse'}
            </div>
            <div className="text-[11px] text-fg-dim mt-1">
              PNG, JPG, WebP, GIF, or SVG · max 2 MB
            </div>
          </div>

          {isUpload && (
            <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-status-online/5 border border-status-online/25 text-xs">
              <span className="text-status-online">Using uploaded image</span>
              <button type="button" className="btn-quiet btn-sm !h-7 !text-[11px]" onClick={removeUpload} disabled={busy}>
                Remove upload
              </button>
            </div>
          )}

          <div className="text-[11px] text-fg-dim">— or —</div>

          <input
            className="input font-mono text-xs"
            value={isUpload ? '' : logoUrl}
            onChange={(e) => setLogoUrl(e.target.value)}
            placeholder="https://example.com/logo.png"
            maxLength={1024}
            disabled={isUpload}
          />
          <p className="text-[11px] text-fg-dim">
            Paste an external URL instead. Saving an external URL clears any uploaded image.
            Leave blank to use the built-in logo.
          </p>
        </div>

        <div>
          <label className="label flex items-center justify-between">
            <span>Accent color</span>
            <button
              type="button"
              className="btn-quiet btn-sm !h-6 !text-[11px] !px-2 normal-case font-normal tracking-normal"
              onClick={() => setAccent(DEFAULT_ACCENT)}
              disabled={accent.toLowerCase() === DEFAULT_ACCENT}
            >
              Reset to default
            </button>
          </label>
          <div className="flex flex-wrap items-center gap-1.5 mb-2">
            {ACCENT_PRESETS.map((p) => {
              const active = accent.toLowerCase() === p.hex.toLowerCase();
              return (
                <button
                  key={p.hex}
                  type="button"
                  onClick={() => setAccent(p.hex)}
                  title={`${p.label} · ${p.hex}`}
                  className={`w-7 h-7 rounded-full border-2 cursor-pointer transition-transform ${
                    active ? 'border-fg scale-110' : 'border-bg-border hover:border-bg-ring'
                  }`}
                  style={{ background: p.hex }}
                  aria-label={p.label}
                  aria-pressed={active}
                />
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={accent}
              onChange={(e) => setAccent(e.target.value)}
              className="w-9 h-9 rounded-md border border-bg-border bg-bg-raised cursor-pointer p-0.5"
              aria-label="Custom color"
            />
            <input
              type="text"
              value={accent}
              onChange={(e) => {
                const v = e.target.value.trim();
                if (/^#?[0-9a-fA-F]{0,6}$/.test(v)) {
                  setAccent(v.startsWith('#') || v === '' ? v : `#${v}`);
                }
              }}
              maxLength={7}
              className="input w-32 font-mono text-xs uppercase"
              placeholder="#34d399"
            />
            <p className="text-[11px] text-fg-dim">Status colors (online green, error red, etc.) stay fixed.</p>
          </div>
        </div>

        <div className="rounded-lg border border-bg-border bg-bg-raised p-3 flex items-center gap-3">
          <div className="text-xs text-fg-dim w-16">Preview</div>
          <div className="flex items-center gap-2.5 flex-1 min-w-0">
            <LogoPreview url={logoUrl} />
            <div className="min-w-0">
              <div className="font-semibold text-fg truncate text-sm">{name || 'Clean Panel'}</div>
              <div className="text-[10px] text-fg-dim truncate">{tagline || 'Rust server control'}</div>
            </div>
            <div className="ml-auto flex items-center gap-2 shrink-0">
              <span className="text-[10px] uppercase tracking-wider text-fg-dim">Accent</span>
              <button type="button" className="btn-primary btn-sm !h-7 !px-3 !text-[11px] cursor-default" tabIndex={-1}>Sample</button>
              <span className="dot bg-status-online" title="Status (unchanged)" />
            </div>
          </div>
        </div>

        {error && <div className="text-sm text-status-error px-3 py-2 rounded-md bg-status-error/10 border border-status-error/30">{error}</div>}

        <div className="flex justify-end gap-2 items-center">
          {savedAt && !dirty && <span className="text-[11px] text-status-online">Saved</span>}
          <button className="btn-primary" disabled={!dirty || busy} onClick={save}>
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </section>
  );
}

function DiscordTab() {
  const [guildId, setGuildId] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    api.get<{ discord_guild_id: string }>('/api/panel/config')
      .then((r) => { setGuildId(r.discord_guild_id ?? ''); setLoaded(true); })
      .catch((e) => setError((e as Error).message));
  }, []);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.patch('/api/panel/config', { discord_guild_id: guildId.trim() });
      setSavedAt(Date.now());
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) return <p className="text-fg-muted text-sm">Loading…</p>;

  return (
    <section className="space-y-4 max-w-2xl">
      <div className="card p-5 space-y-3">
        <header>
          <h3 className="font-semibold text-fg">Discord guild</h3>
          <p className="text-sm text-fg-muted mt-1">
            ID of the guild new logins must belong to. Leave blank to allow any
            Discord account through OAuth (login is still gated by the user
            allowlist).
          </p>
        </header>

        <div>
          <label className="label">Guild ID</label>
          <input
            className="input font-mono"
            value={guildId}
            onChange={(e) => setGuildId(e.target.value.replace(/[^0-9]/g, ''))}
            placeholder="123456789012345678"
            maxLength={32}
          />
        </div>

        {error && <div className="text-sm text-status-error px-3 py-2 rounded-md bg-status-error/10 border border-status-error/30">{error}</div>}

        <div className="flex justify-end gap-2 items-center">
          {savedAt && <span className="text-[11px] text-status-online">Saved</span>}
          <button className="btn-primary" disabled={busy} onClick={save}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
      <div className="card p-5">
        <h3 className="font-semibold text-fg">Pop bots</h3>
        <p className="text-sm text-fg-muted mt-1">
          Each server can run its own Discord status bot — toggle it from the
          server's <strong>Settings</strong> tab and provide a bot token there.
          The bot's status updates every ~10s with the live player count.
        </p>
      </div>
    </section>
  );
}

function ShieldIcon() {
  return (
    <svg
      width={14} height={14} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round"
      aria-hidden className="shrink-0"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function MachinesTab({ isAdmin }: { isAdmin: boolean }) {
  const [machines, setMachines] = useState<Machine[] | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [created, setCreated] = useState<{ machine_name: string; bootstrap_token: string; hub_url: string } | null>(null);
  const [toDelete, setToDelete] = useState<Machine | null>(null);

  const reload = () => api.get<{ machines: Machine[] }>('/api/machines').then((r) => setMachines(r.machines));
  useEffect(() => { reload(); }, []);

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-fg-muted">Each Rust machine runs one Node Agent connected back to the Hub.</p>
        {isAdmin && (
          <button className="btn-primary btn-sm" onClick={() => setShowAdd(true)}>
            <IconPlus className="w-3.5 h-3.5" /> Register Machine
          </button>
        )}
      </div>

      {machines === null ? (
        <p className="text-fg-muted text-sm">Loading…</p>
      ) : (
        <div className="card overflow-hidden">
          {machines.length === 0 ? (
            <div className="p-10 flex flex-col items-center text-center gap-2">
              <div className="w-12 h-12 rounded-full bg-bg-raised border border-bg-border flex items-center justify-center text-fg-dim">
                <IconActivity className="w-5 h-5" />
              </div>
              <span className="text-sm text-fg-muted">No machines yet</span>
            </div>
          ) : (
            <ul className="divide-y divide-bg-border">
              {machines.map((m) => (
                <li key={m.machine_name} className="px-4 py-3 flex items-center gap-3">
                  <span className={`dot dot-lg shrink-0 ${m.online ? 'bg-status-online' : 'bg-status-offline'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-fg truncate">{m.machine_name}</div>
                    <div className="text-xs text-fg-dim">
                      {m.online ? `online · agent v${m.agent_version || '?'}` : `last seen ${m.last_seen ? relTime(m.last_seen) : 'never'}`}
                      {' · '}{m.server_count} server{m.server_count === 1 ? '' : 's'}
                    </div>
                  </div>
                  {isAdmin && (
                    <button
                      onClick={() => setToDelete(m)}
                      className="btn-ghost btn-sm hover:!text-status-error"
                      disabled={m.server_count > 0}
                      title={m.server_count > 0 ? 'Move or delete attached servers first' : 'Remove machine'}
                    >
                      Remove
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {showAdd && (
        <RegisterMachineModal
          onCancel={() => setShowAdd(false)}
          onCreated={(info) => { setShowAdd(false); setCreated(info); reload(); }}
        />
      )}

      {toDelete && (
        <DeleteMachineModal
          machine={toDelete}
          onCancel={() => setToDelete(null)}
          onDeleted={() => { setToDelete(null); reload(); }}
        />
      )}

      {created && (
        <div className="card !border-accent/30 !bg-accent/5 p-5 space-y-3 animate-slide-up">
          <h3 className="font-semibold text-fg">Machine registered: <span className="text-accent">{created.machine_name}</span></h3>
          <p className="text-sm text-fg-muted">Run this on the new machine in an elevated PowerShell:</p>
          <pre className="bg-bg p-3 rounded-lg font-mono text-[11px] overflow-x-auto select-all border border-bg-border">
{`.\\install.ps1 ${created.machine_name}`}
          </pre>
          <p className="text-xs text-fg-muted">
            Run from <code className="bg-bg-raised border border-bg-border px-1 rounded">node-agent\</code> in an elevated PowerShell.
            Hub URL, bootstrap token, Mongo URI, and R2 credentials are read from <code className="bg-bg-raised border border-bg-border px-1 rounded">agent-defaults.env</code> in the project root.
          </p>
          <button onClick={() => setCreated(null)} className="btn-ghost btn-sm">Dismiss</button>
        </div>
      )}
    </section>
  );
}

function RegisterMachineModal({ onCancel, onCreated }: {
  onCancel: () => void;
  onCreated: (info: { machine_name: string; bootstrap_token: string; hub_url: string }) => void;
}) {
  const [name, setName] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="fixed inset-0 z-50 bg-bg/70 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" onClick={onCancel}>
      <div className="card p-6 w-full max-w-md shadow-modal animate-slide-up" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <h3 className="text-lg font-semibold mb-4">Register Machine</h3>
        <div className="space-y-3">
          <div>
            <label className="label">Machine Name (unique, no spaces)</label>
            <input className="input font-mono" value={name} onChange={(e) => setName(e.target.value)} placeholder="EU_PHYS_9800X3D" autoFocus />
          </div>
          <div>
            <label className="label">Label (optional)</label>
            <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="EU primary 9800X3D / 64GB" />
          </div>
          {error && (
            <div className="text-sm text-status-error px-3 py-2 rounded-md bg-status-error/10 border border-status-error/30">
              {error}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button className="btn-ghost" onClick={onCancel}>Cancel</button>
          <button
            className="btn-primary"
            disabled={busy || !name}
            onClick={async () => {
              setBusy(true);
              try {
                const res = await api.post<{ machine_name: string; bootstrap_token: string; hub_url: string }>(
                  '/api/machines',
                  { machine_name: name, machine_label: label },
                );
                onCreated(res);
              } catch (err) { setError((err as Error).message); setBusy(false); }
            }}
          >
            Register
          </button>
        </div>
      </div>
    </div>
  );
}

function UsersTab({ me }: { me: { discord_id: string; role: string } }) {
  const [users, setUsers] = useState<User[] | null>(null);
  useEffect(() => {
    if (me.role !== 'admin') return;
    api.get<{ users: User[] }>('/api/system/users').then((r) => setUsers(r.users));
  }, [me.role]);

  if (me.role !== 'admin') {
    return <p className="text-fg-muted text-sm">Admin only.</p>;
  }

  return (
    <section className="space-y-4">
      <p className="text-sm text-fg-muted">
        Users who have logged in. Roles:{' '}
        <strong className="text-fg">admin</strong> (everything),{' '}
        <strong className="text-fg">operator</strong> (actions),{' '}
        <strong className="text-fg">viewer</strong> (read-only).
      </p>

      {users === null ? <p className="text-fg-muted text-sm">Loading…</p> : (
        <div className="card overflow-hidden">
          <ul className="divide-y divide-bg-border">
            {users.map((u) => (
              <li key={u.discord_id} className="px-4 py-3 flex items-center gap-3 flex-wrap sm:flex-nowrap">
                <UserAvatar user={u} />

                <div className="flex-1 min-w-0">
                  <div className="font-medium text-fg truncate">{u.discord_username}</div>
                  <div className="text-xs text-fg-dim truncate">
                    {u.discord_id} · joined {fmtDate(u.added_at)}{u.last_login && ` · last login ${relTime(u.last_login)}`}
                  </div>
                </div>
                <select
                  className="input w-32"
                  value={u.role}
                  disabled={u.discord_id === me.discord_id}
                  onChange={async (e) => {
                    await api.patch(`/api/system/users/${u.discord_id}`, { role: e.target.value });
                    setUsers((cur) => cur ? cur.map((x) => x.discord_id === u.discord_id ? { ...x, role: e.target.value as User['role'] } : x) : cur);
                  }}
                >
                  <option value="admin">admin</option>
                  <option value="operator">operator</option>
                  <option value="viewer">viewer</option>
                </select>
                <button
                  disabled={u.discord_id === me.discord_id}
                  onClick={async () => {
                    if (!confirm(`Remove ${u.discord_username}?`)) return;
                    await api.delete(`/api/system/users/${u.discord_id}`);
                    setUsers((cur) => cur ? cur.filter((x) => x.discord_id !== u.discord_id) : cur);
                  }}
                  className="btn-ghost btn-sm disabled:opacity-30"
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function DeleteMachineModal({ machine, onCancel, onDeleted }: {
  machine: Machine;
  onCancel: () => void;
  onDeleted: () => void;
}) {
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const matches = confirmation.trim() === machine.machine_name;

  const submit = async () => {
    if (!matches) return;
    setBusy(true);
    setError(null);
    try {
      await api.delete(`/api/machines/${encodeURIComponent(machine.machine_name)}`);
      onDeleted();
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-bg/70 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" onClick={onCancel}>
      <div
        className="card p-6 w-full max-w-md shadow-modal animate-slide-up !border-status-error/30"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-machine-title"
      >
        <h3 id="delete-machine-title" className="text-lg font-semibold mb-2 flex items-center gap-2">
          <span className="text-status-error">Delete machine</span>
          <span className="text-fg font-mono text-base">{machine.machine_name}</span>
        </h3>
        <p className="text-sm text-fg-muted mb-4">
          This permanently removes the machine record from the Hub. The agent will no longer be able to authenticate and reconnect.
          {machine.server_count > 0 && (
            <>
              <br /><br />
              <span className="text-status-warn">
                {machine.server_count} server{machine.server_count === 1 ? ' is' : 's are'} still attached. Move or delete them first.
              </span>
            </>
          )}
        </p>

        <label className="label">
          Type <span className="font-mono text-fg">{machine.machine_name}</span> to confirm
        </label>
        <input
          className="input font-mono"
          autoFocus
          value={confirmation}
          onChange={(e) => setConfirmation(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && matches && submit()}
          placeholder={machine.machine_name}
        />

        {error && (
          <div className="mt-3 text-sm text-status-error px-3 py-2 rounded-md bg-status-error/10 border border-status-error/30">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-5">
          <button className="btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button
            className="btn-danger"
            disabled={!matches || busy || machine.server_count > 0}
            onClick={submit}
          >
            {busy ? 'Deleting…' : 'Delete machine'}
          </button>
        </div>
      </div>
    </div>
  );
}

function UserAvatar({ user, size = 36 }: { user: User; size?: number }) {
  const [failed, setFailed] = useState(false);
  const initial = (user.discord_username[0] ?? '?').toUpperCase();
  const src = user.avatar_url && !failed ? user.avatar_url : null;
  return (
    <div
      className="rounded-full bg-bg-raised border border-bg-border flex items-center justify-center text-fg text-sm font-medium shrink-0 overflow-hidden"
      style={{ width: size, height: size }}
      aria-hidden
    >
      {src ? (
        <img src={src} alt="" className="w-full h-full object-cover" onError={() => setFailed(true)} />
      ) : (
        <span>{initial}</span>
      )}
    </div>
  );
}

// -------------------------------------------------------------------
// Security tab — 2FA enrollment + management
// -------------------------------------------------------------------

interface TwoFactorStatus { enabled: boolean; recovery_codes_remaining: number }

function SecurityTab() {
  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [setup, setSetup] = useState<{ secret: string; otpauth: string; qr: string } | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [showDisable, setShowDisable] = useState(false);

  const reload = () => api.get<TwoFactorStatus>('/api/auth/2fa/status').then(setStatus);
  useEffect(() => { reload(); }, []);

  const startSetup = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ secret: string; otpauth: string; qr: string }>('/api/auth/2fa/setup');
      setSetup(r);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const confirmSetup = async () => {
    if (!setup) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ ok: true; recovery_codes: string[] }>('/api/auth/2fa/confirm', { secret: setup.secret, code: code.trim() });
      setRecoveryCodes(r.recovery_codes);
      setSetup(null);
      setCode('');
      reload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/auth/2fa/disable', { code: code.trim() });
      setShowDisable(false);
      setCode('');
      setRecoveryCodes(null);
      reload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (!status) return <p className="text-fg-muted text-sm">Loading…</p>;

  return (
    <section className="space-y-4 max-w-2xl">
      <PushCard />
      <div className="card p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-semibold text-fg">Two-step verification</h3>
            <p className="text-sm text-fg-muted mt-1">
              Adds a one-time code from an authenticator app on top of Discord login. Recommended for admin accounts.
            </p>
          </div>
          {status.enabled ? (
            <span className="text-[11px] font-medium px-2 py-0.5 rounded-md bg-status-online/10 text-status-online ring-1 ring-status-online/30 inline-flex items-center gap-1 shrink-0">
              <IconCheck className="w-3 h-3" /> Enabled
            </span>
          ) : (
            <span className="text-[11px] font-medium px-2 py-0.5 rounded-md bg-bg-raised text-fg-muted ring-1 ring-bg-border shrink-0">Disabled</span>
          )}
        </div>

        {!status.enabled && !setup && (
          <button className="btn-primary btn-sm" onClick={startSetup} disabled={busy}>
            {busy ? 'Starting…' : 'Enable two-step verification'}
          </button>
        )}

        {setup && (
          <div className="space-y-3 border-t border-bg-border pt-4">
            <p className="text-sm text-fg-muted">
              Scan this QR with Google Authenticator, Authy, 1Password, or any TOTP app, then enter the 6-digit code it shows to confirm.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 items-start">
              <img
                src={setup.qr}
                alt="Authenticator QR code"
                className="w-44 h-44 rounded-lg border border-bg-border bg-white p-1"
              />
              <div className="flex-1 min-w-0 space-y-2">
                <div>
                  <div className="label !mb-1">Or enter this secret manually</div>
                  <code className="font-mono text-xs bg-bg-raised border border-bg-border rounded px-2 py-1 break-all block">
                    {setup.secret}
                  </code>
                </div>
                <div>
                  <label className="label">Enter the 6-digit code</label>
                  <input
                    className="input font-mono text-center tracking-[0.4em] !text-base"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    autoFocus
                  />
                </div>
                <div className="flex gap-2 pt-1">
                  <button className="btn-ghost btn-sm" onClick={() => { setSetup(null); setCode(''); setError(null); }} disabled={busy}>
                    Cancel
                  </button>
                  <button className="btn-primary btn-sm" onClick={confirmSetup} disabled={busy || code.trim().length < 6}>
                    {busy ? 'Confirming…' : 'Confirm and enable'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {recoveryCodes && (
          <div className="border-t border-bg-border pt-4 space-y-2">
            <div className="text-sm font-medium text-fg">Recovery codes</div>
            <p className="text-xs text-fg-muted">
              Store these somewhere safe. Each one can be used once if you lose access to your authenticator.
              They will not be shown again.
            </p>
            <div className="grid grid-cols-2 gap-1.5 font-mono text-xs bg-bg-raised border border-bg-border rounded-md p-3">
              {recoveryCodes.map((c) => (
                <div key={c} className="text-fg select-all">{c}</div>
              ))}
            </div>
            <button
              className="btn-ghost btn-sm"
              onClick={() => navigator.clipboard.writeText(recoveryCodes.join('\n')).catch(() => {})}
            >
              Copy all
            </button>
          </div>
        )}

        {status.enabled && !showDisable && (
          <div className="border-t border-bg-border pt-4 space-y-2">
            <div className="text-xs text-fg-dim">
              {status.recovery_codes_remaining} recovery code{status.recovery_codes_remaining === 1 ? '' : 's'} remaining
            </div>
            <button className="btn-danger btn-sm" onClick={() => { setShowDisable(true); setError(null); }}>
              Disable two-step verification
            </button>
          </div>
        )}

        {showDisable && (
          <div className="border-t border-bg-border pt-4 space-y-2">
            <p className="text-sm text-fg-muted">Enter your current 6-digit code (or a recovery code) to confirm.</p>
            <input
              className="input font-mono text-center tracking-[0.4em] !text-base max-w-xs"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoFocus
              maxLength={11}
              placeholder="000000"
            />
            <div className="flex gap-2">
              <button className="btn-ghost btn-sm" onClick={() => { setShowDisable(false); setCode(''); setError(null); }} disabled={busy}>Cancel</button>
              <button className="btn-danger btn-sm" onClick={disable} disabled={busy || !code.trim()}>
                {busy ? 'Disabling…' : 'Disable 2FA'}
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="text-sm text-status-error px-3 py-2 rounded-md bg-status-error/10 border border-status-error/30">
            {error}
          </div>
        )}
      </div>
    </section>
  );
}

function PushCard() {
  const [state, setState] = useState<PushState>('idle');
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!pushSupported()) { setState('unsupported'); setBusy(false); return; }
    getCurrentState().then(setState).finally(() => setBusy(false));
  }, []);

  const onToggle = async () => {
    setBusy(true);
    setError(null);
    try {
      if (state === 'subscribed') {
        setState(await unsubscribe());
      } else {
        setState(await subscribe());
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const subtitle =
    state === 'unsupported' ? 'Your browser does not support push notifications.'
      : state === 'denied'   ? 'Notifications are blocked. Re-enable them in your browser site settings to opt in.'
      : state === 'subscribed' ? 'This device is registered. You will receive crash and offline alerts even when this tab is closed.'
      : 'Get a desktop notification when an agent goes offline or a server crashes — even when this tab is in the background.';

  return (
    <div className="card p-5 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold text-fg">Browser notifications</h3>
          <p className="text-sm text-fg-muted mt-1 max-w-md">{subtitle}</p>
        </div>
        {state === 'subscribed' && (
          <span className="text-[11px] font-medium px-2 py-0.5 rounded-md bg-status-online/10 text-status-online ring-1 ring-status-online/30 inline-flex items-center gap-1 shrink-0">
            <IconCheck className="w-3 h-3" /> Registered
          </span>
        )}
      </div>
      {error && (
        <div className="text-sm text-status-error px-3 py-2 rounded-md bg-status-error/10 border border-status-error/30">
          {error}
        </div>
      )}
      {state === 'unsupported' || state === 'denied' ? null : (
        <button
          className={state === 'subscribed' ? 'btn-ghost btn-sm' : 'btn-primary btn-sm'}
          onClick={onToggle}
          disabled={busy}
        >
          {busy ? '…' : state === 'subscribed' ? 'Disable on this device' : 'Enable on this device'}
        </button>
      )}
    </div>
  );
}

function SystemTab() {
  const [info, setInfo] = useState<Record<string, unknown> | null>(null);
  useEffect(() => {
    api.get<Record<string, unknown>>('/api/system/info').then(setInfo).catch(() => {});
  }, []);
  if (!info) return <p className="text-fg-muted text-sm">Loading…</p>;

  return (
    <pre className="card p-5 font-mono text-xs whitespace-pre-wrap text-fg-muted leading-relaxed">
      {JSON.stringify(info, null, 2)}
    </pre>
  );
}
